/**
 * Cliente del backend de Androcles (FastAPI, puerto 8300).
 *
 * Contrato unico entre kiosco y nucleo. Reglas:
 *  - Ningun componente hace `fetch` por su cuenta: todo pasa por aqui.
 *  - Los tipos reflejan el schema `tesoreria` y los enums de `backend.models`.
 *  - Nada de secretos: la API key de Anthropic vive en crypto_keys.ini,
 *    en el servidor. El kiosco solo habla con :8300 por Tailscale/localhost.
 *  - Las herramientas de Nivel 2 NO se ejecutan desde el kiosco: el kiosco
 *    solo lista, muestra y resuelve confirmaciones ya propuestas por el agente.
 *
 * Rutas: verificadas contra los routers reales de `backend/api` (verificacion
 * de esqueleto, 2026-07-24). Lo que el backend aun no sirve queda marcado con
 * TODO(FaseN) y lanza `ErrorApi` 404 en vez de fingir que existe.
 *
 * TODO(Fase1): reconciliar tambien las FORMAS de respuesta. Los adaptadores
 *              `_a*` de mas abajo traducen el contrato del backend al que
 *              esperan los componentes; cuando el kiosco este cerrado con el
 *              usuario real conviene unificar y borrar los adaptadores.
 */

// ---------------------------------------------------------------------------
// Configuracion de transporte
// ---------------------------------------------------------------------------

/** Base del API. Vacio => rutas relativas y proxy de Vite hacia :8300. */
const BASE_API: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/** Base WebSocket. Vacio => se deriva del origen de la pagina. */
const BASE_WS: string = (import.meta.env.VITE_WS_BASE as string | undefined) ?? '';

/** Error homogeneo de API: conserva el codigo HTTP para distinguir el 403 de N2. */
export class ErrorApi extends Error {
  constructor(
    public readonly estado: number,
    mensaje: string,
    public readonly detalle?: unknown,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }

  /** true si el backend rechazo por falta de confirmacion verbal (Nivel 2). */
  get esConfirmacionRequerida(): boolean {
    return this.estado === 403;
  }

  /** true si no hay sesion o ha caducado: hay que volver a la pantalla de entrada. */
  get esSesionCaducada(): boolean {
    return this.estado === 401;
  }
}

// ---------------------------------------------------------------------------
// Sesion: aviso central de caducidad
// ---------------------------------------------------------------------------

/**
 * Aviso que se dispara en CUALQUIER 401 del backend.
 *
 * `App` lo usa para pintar la pantalla de entrada sin desmontar nada: el
 * dictado a medias y la conversacion siguen en memoria y reaparecen intactos
 * al volver a entrar. Se llama una vez por 401, sin acumular manejadores.
 */
let _alCaducarSesion: (() => void) | null = null;

/** Registra (o borra, con `null`) el manejador global de sesion caducada. */
export function alCaducarSesion(manejador: (() => void) | null): void {
  _alCaducarSesion = manejador;
}

function _avisarSesionCaducada(): void {
  try {
    _alCaducarSesion?.();
  } catch {
    // Un fallo pintando el login no debe tapar el error original.
  }
}

/** Realiza una peticion JSON contra el backend y tipa la respuesta. */
async function peticion<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const respuesta = await fetch(`${BASE_API}/api${ruta}`, {
    headers: { 'Content-Type': 'application/json', ...(opciones.headers ?? {}) },
    credentials: 'include',
    ...opciones,
  });

  if (!respuesta.ok) {
    let detalle: unknown;
    try {
      detalle = await respuesta.json();
    } catch {
      detalle = await respuesta.text().catch(() => undefined);
    }
    const mensaje =
      (detalle as { detail?: string } | undefined)?.detail ??
      `El servicio respondio ${respuesta.status}`;
    // 401 = sin sesion o caducada. Se avisa una sola vez, aqui, para que
    // ningun componente tenga que acordarse de comprobarlo. Se excluyen los
    // propios endpoints de entrada: un login fallido no es una sesion
    // caducada, y decirselo al jefe solo le confundiria.
    if (respuesta.status === 401 && !ruta.startsWith('/auth/')) _avisarSesionCaducada();
    throw new ErrorApi(respuesta.status, mensaje, detalle);
  }

  if (respuesta.status === 204) return undefined as T;
  return (await respuesta.json()) as T;
}

/**
 * Igual que `peticion`, pero SIN el prefijo `/api`.
 * Solo lo necesita `/salud`, que cuelga de la raiz a proposito: un health
 * check no debe depender de que el resto de la API este montada.
 */
async function peticionRaiz<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const respuesta = await fetch(`${BASE_API}${ruta}`, {
    headers: { 'Content-Type': 'application/json', ...(opciones.headers ?? {}) },
    credentials: 'include',
    ...opciones,
  });
  if (!respuesta.ok) {
    throw new ErrorApi(respuesta.status, `El servicio respondio ${respuesta.status}`);
  }
  return (await respuesta.json()) as T;
}

/** Construye una URL WebSocket absoluta a partir de una ruta relativa. */
export function urlWebSocket(ruta: string): string {
  if (BASE_WS) return `${BASE_WS}${ruta}`;
  const protocolo = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocolo}//${window.location.host}${ruta}`;
}

// ---------------------------------------------------------------------------
// Tipos del dominio (espejo de backend.models)
// ---------------------------------------------------------------------------

export type SignoFlujo = 'cobro' | 'pago' | 'neutro';
export type ClasificadoPor = 'regla' | 'agente' | 'humano' | 'sin_clasificar';
export type EstadoPrevision = 'pendiente' | 'conciliada' | 'vencida' | 'cancelada';
export type EstadoConfirmacion = 'pendiente' | 'confirmada' | 'rechazada' | 'caducada';
export type RolTurno = 'jefe' | 'agente' | 'sistema';
export type ModoSesion = 'conversacion' | 'secretaria' | 'despacho';
export type CanalSesion = 'kiosco' | 'telefono' | 'texto' | 'cron';
export type AgenteRol = 'orquestador' | 'analista' | 'contable' | 'asesor' | 'despacho_diario';

/** Saldo de una cuenta bancaria dentro de la posicion consolidada. */
export interface SaldoCuenta {
  cuenta_id: number;
  alias: string;
  entidad: string;
  iban_visible: string; // Solo los 4 ultimos digitos; el backend censura el resto.
  saldo_actual: number;
  saldo_fecha: string; // AAAA-MM-DD
  saldo_seguridad: number | null;
  /** true si `saldo_actual` esta por debajo del umbral de seguridad. */
  bajo_umbral: boolean;
}

/** Posicion de una empresa del grupo. */
export interface PosicionEmpresa {
  empresa_id: number;
  codigo: string;
  nombre: string;
  saldo_total: number;
  saldo_seguridad: number | null;
  bajo_umbral: boolean;
  cuentas: SaldoCuenta[];
}

/** Respuesta de la herramienta `posicion(fecha, empresa?)`. */
export interface Posicion {
  fecha: string;
  saldo_consolidado: number;
  divisa: string;
  empresas: PosicionEmpresa[];
  /** Fecha del ultimo extracto N43 incorporado: mide la frescura del dato. */
  ultima_ingesta: string | null;
}

/** Punto de la curva de caja devuelta por `prevision(horizonte, escenario?)`. */
export interface PuntoPrevision {
  fecha: string; // AAAA-MM-DD
  saldo_previsto: number;
  /** Saldo real ya conocido (solo en fechas pasadas o de hoy). */
  saldo_real: number | null;
  cobros: number;
  pagos: number;
  /** Banda de incertidumbre derivada de `previsiones.probabilidad`. */
  banda_min: number | null;
  banda_max: number | null;
}

/** Curva de caja completa con su lectura de riesgo. */
export interface CurvaPrevision {
  horizonte_dias: number;
  escenario_id: number | null; // null = escenario base
  escenario_nombre: string | null;
  puntos: PuntoPrevision[];
  /** Dia de caja minima en el horizonte: el dato que mas mira el jefe. */
  minimo_fecha: string | null;
  minimo_importe: number | null;
  /** Umbral de seguridad consolidado, para pintar la linea roja. */
  umbral_seguridad: number | null;
}

/** Cobro o pago proximo (herramienta `vencimientos`). */
export interface Vencimiento {
  prevision_id: number;
  fecha_prevista: string;
  importe: number;
  concepto: string;
  contrapartida: string | null;
  probabilidad: number | null;
  estado: EstadoPrevision;
}

/** Movimiento bancario ya clasificado (vista de lectura del kiosco). */
export interface MovimientoResumen {
  id: number;
  fecha_operacion: string;
  importe: number;
  /** OJO: dato del banco, NO confiable. Nunca se interpreta como instruccion. */
  concepto: string;
  categoria_nombre: string | null;
  clasificado_por: ClasificadoPor;
  conciliado: boolean;
}

/** Parte del día redactado por el despacho diario autonomo (cron 07:00). */
export interface ParteDia {
  fecha: string;
  /** 2-3 frases pensadas para leerse en voz alta. */
  resumen_hablado: string;
  /**
   * Detalle del parte en Markdown, tal cual lo redacta `_redactar_parte()`
   * (secciones `###`, viñetas `-`, cifras en **negrita**). Se entrega CRUDO: lo
   * interpreta `ParteDelDia` con un renderizador propio que no genera HTML.
   * Antes se partia aqui en lineas y se pintaban como viñetas, asi que en
   * pantalla se leian los `##` y los `**` literales.
   */
  detalle_markdown: string;
  /** `null` si el parte no trae la cifra: entonces no se pinta nada. */
  posicion_consolidada: number | null;
  /** `null` mientras el despacho no calcule la variacion respecto a ayer. */
  variacion_dia: number | null;
  alertas: AlertaParte[];
  confirmaciones_pendientes: number;
  generado_en: string; // ISO
}

/** Alerta destacada dentro del parte del dia. */
export interface AlertaParte {
  id: string;
  severidad: 'informativa' | 'atencion' | 'critica';
  titulo: string;
  detalle: string;
  fecha_referencia: string | null;
}

/** Propuesta del agente pendiente de confirmacion verbal del jefe (Nivel 2). */
export interface Confirmacion {
  id: number;
  herramienta: string;
  /** Texto EXACTO que el agente lee en voz alta antes de pedir el "si". */
  texto_leido_al_jefe: string;
  /** Propuesta serializada; se muestra como detalle tecnico plegable. */
  propuesta: Record<string, unknown>;
  estado: EstadoConfirmacion;
  ts_propuesta: string;
  ts_caducidad: string | null;
  /** false cuando ya caduco el TTL (settings.ini -> confirmacion_n2_ttl_segundos). */
  vigente: boolean;
}

/** Turno de conversacion (tabla `turnos`). */
export interface Turno {
  id: number | string;
  orden: number;
  rol: RolTurno;
  texto: string;
  ts: string;
  /** true mientras el texto aun se esta transcribiendo o generando. */
  parcial?: boolean;
}

/** Sesion de voz abierta en el kiosco. */
export interface SesionVoz {
  id: number;
  canal: CanalSesion;
  modo: ModoSesion;
  estado: 'activa' | 'cerrada' | 'abortada';
  iniciada_en: string;
}

/** Configuracion del avatar publicada por el backend (settings.ini -> [avatar]). */
export interface ConfigAvatar {
  activo: boolean;
  servicio_url: string; // ws://127.0.0.1:8301/stream
  fps_minimo: number;
  modo_degradado: boolean;
  /**
   * Presencia que pinta el kiosco: `onda` (grafico de sonido, sin GPU) o
   * `cara` (retrato con MuseTalk). Lo decide el backend en settings.ini.
   */
  modo: 'onda' | 'cara';
  /** Ruta del retrato estatico usado en modo degradado. */
  retrato_url: string;
}

/** Una voz que se puede probar desde el kiosco. */
export interface OpcionVoz {
  motor: string;
  voz: string;
  etiqueta: string;
  /** false si falta descargar el modelo o la clave: se pinta en gris. */
  disponible: boolean;
  /** Por que no se puede usar, en castellano. Vacio si esta lista. */
  motivo: string;
  /**
   * true si esa voz manda el texto FUERA de esta maquina.
   *
   * Lo que se lee en voz alta es la respuesta entera, cifras incluidas: con el
   * cerebro local puesto por privacidad, una voz en la nube deshace media
   * medida sin que se note. Tiene que verse al elegirla.
   */
  en_la_nube: boolean;
}

/** Voz que se usara en la proxima frase. */
export interface VozActiva {
  motor: string;
  voz: string;
  /** true si viene de una prueba manual y no de settings.ini. */
  manual: boolean;
}

/** Resultado de cerrar un dictado en modo secretaria. */
export interface ResultadoDictado {
  slug: string;
  ruta_nota: string;
  texto_literal: string;
  texto_pulido: string | null;
  /** Ruta del .docx si el jefe pidio exportacion a Word. */
  ruta_docx: string | null;
}

/** Una instruccion guardada por el jefe (biblioteca del panel de ajustes). */
export interface InstruccionIA {
  /** Nombre corto con el que la reconoce ("Trato", "Nada de rodeos"…). */
  nombre: string;
  texto: string;
  /** Solo las activas se inyectan en el contexto de cada turno. */
  activa: boolean;
}

/**
 * Ajustes de la IA que el jefe puede cambiar sin tocar `settings.ini`.
 * Espejo EXACTO del dataclass `backend.agente.preferencias.Preferencias`:
 * los nombres van en snake_case porque son los que valida el backend.
 */
export interface AjustesIA {
  /** El cerebro que CONVERSA con el jefe. */
  modelo_orquestador: string;
  /** Los otros cuatro roles. Vacio = manda `settings.ini`. */
  modelo_asesor: string;
  modelo_contable: string;
  modelo_nlu_voz: string;
  modelo_consejero_senior: string;
  /** Servidor de Ollama. Solo se admite loopback o red local (lo valida el backend). */
  ollama_url: string;
  /** `num_ctx`: cuanta conversacion le cabe al cerebro local. */
  ollama_contexto: number;
  ollama_timeout_s: number;
  /** Dejar que razone en voz alta antes de responder (el kiosco lo leeria). */
  ollama_pensar: boolean;
  /** 0 = siempre lo mas probable; 1 = mas variado. Ver `temperatura_aplicable`. */
  temperatura: number;
  max_iteraciones: number;
  frases_respuesta: number;
  contexto_adelantado: boolean;
  memoria_activa: boolean;
  motor_voz: string;
  voz: string;
  voz_en_streaming: boolean;
  instrucciones: InstruccionIA[];
}

/** Un cerebro de los cinco, tal y como lo describe el backend. */
export interface RolModelo {
  /** Clave interna (`orquestador`, `asesor`…): es la que se manda al guardar. */
  clave: string;
  /** Como se le llama al jefe ("Hablar con usted"). */
  etiqueta: string;
  /** Modelo que tiene puesto ahora mismo. */
  modelo: string;
}

/** Entre que valores puede elegir el panel. Lo decide el backend, no el kiosco. */
export interface OpcionesAjustes {
  modelos: string[];
  /** Los cinco cerebros, en orden de importancia para el jefe. */
  roles: RolModelo[];
  /**
   * false cuando el modelo GUARDADO ya no admite `temperature` (deprecado en
   * los modelos nuevos: mandarlo devuelve 400 y deja el kiosco mudo). El panel
   * lo usa para bloquear ese control y explicarlo.
   */
  temperatura_aplicable: boolean;
  voces: OpcionVoz[];
}

/** Respuesta de GET /api/ajustes: lo puesto y entre que se puede elegir. */
export interface RespuestaAjustes {
  ajustes: AjustesIA;
  opciones: OpcionesAjustes;
}

/** Estado del servicio, para el indicador de la barra superior. */
export interface EstadoServicio {
  ok: boolean;
  /** Kill switch: settings.ini -> [agente] agente_activo. */
  agente_activo: boolean;
  bd_ok: boolean;
  version: string;
}

// ---------------------------------------------------------------------------
// API publica agrupada por area
// ---------------------------------------------------------------------------

/** Fecha de hoy en AAAA-MM-DD (hora local del kiosco). */
function hoyISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Fecha de hace `dias` dias en AAAA-MM-DD. */
function haceDiasISO(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Autenticacion
// ---------------------------------------------------------------------------

/** Persona que ha entrado (sin un solo hash: el backend no los expone). */
export interface UsuarioSesion {
  id: number;
  usuario: string;
  nombre: string;
  rol: 'jefe' | 'admin';
  tiene_pin: boolean;
}

/**
 * Que pide este equipo para entrar. **Lo decide el backend**, no el kiosco:
 * 'kiosco' solo si el dispositivo esta registrado en `dispositivos_kiosco`.
 */
export interface ModoAcceso {
  modo: 'kiosco' | 'remoto';
  alias: string | null;
  autenticado: boolean;
  usuario: UsuarioSesion | null;
}

/** Sesion recien abierta. */
export interface SesionAbierta {
  usuario: UsuarioSesion;
  tipo_sesion: 'kiosco' | 'remoto';
  expira_en: string;
}

/** Kiosco registrado (vista de administracion). */
export interface DispositivoKiosco {
  id: number;
  alias: string;
  activo: boolean;
  registrado_en: string;
  ultimo_uso: string | null;
  es_este_equipo: boolean;
}

export const api = {
  // --- Autenticacion ---
  /** GET /api/auth/modo — publico: la pantalla de entrada lo pide sin sesion. */
  modoAcceso: (): Promise<ModoAcceso> => peticion<ModoAcceso>('/auth/modo'),

  /** POST /api/auth/login — usuario + contrasena (sesion corta, 2 h). */
  login: (usuario: string, password: string): Promise<SesionAbierta> =>
    peticion<SesionAbierta>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ usuario, password }),
    }),

  /** POST /api/auth/login-pin — PIN de 6 digitos (solo desde kiosco registrado). */
  loginPin: (pin: string, usuario?: string): Promise<SesionAbierta> =>
    peticion<SesionAbierta>('/auth/login-pin', {
      method: 'POST',
      body: JSON.stringify({ pin, usuario: usuario ?? null }),
    }),

  /** POST /api/auth/logout — cierre logico de la sesion; el kiosco sigue siendo kiosco. */
  logout: (): Promise<{ cerrada: boolean }> =>
    peticion<{ cerrada: boolean }>('/auth/logout', { method: 'POST' }),

  /** GET /api/auth/me — quien esta dentro. */
  yo: (): Promise<UsuarioSesion> => peticion<UsuarioSesion>('/auth/me'),

  /** POST /api/auth/dispositivos — registra ESTE equipo como kiosco (solo admin). */
  registrarKiosco: (alias: string): Promise<DispositivoKiosco> =>
    peticion<DispositivoKiosco>('/auth/dispositivos', {
      method: 'POST',
      body: JSON.stringify({ alias }),
    }),

  /** GET /api/auth/dispositivos — kioscos registrados (solo admin). */
  kioscos: (): Promise<DispositivoKiosco[]> =>
    peticion<DispositivoKiosco[]>('/auth/dispositivos'),

  /**
   * Salud del servicio y estado del kill switch.
   * `/salud` NO lleva prefijo /api (ver `backend.api.registrar_routers`).
   */
  estado: async (): Promise<EstadoServicio> => {
    const s = await peticionRaiz<{
      estado: 'ok' | 'degradado';
      version: string;
      agente_activo: boolean;
      componentes: { nombre: string; ok: boolean }[];
    }>('/salud');
    return {
      ok: s.estado === 'ok',
      agente_activo: s.agente_activo,
      bd_ok: s.componentes.find((c) => c.nombre === 'mariadb')?.ok ?? false,
      version: s.version,
    };
  },

  // --- Tesoreria (Nivel 1, solo lectura) ---
  /** GET /api/posicion — `RespuestaPosicion` de backend/api/posicion.py. */
  posicion: async (fecha?: string, empresaId?: number): Promise<Posicion> => {
    const q = new URLSearchParams();
    if (fecha) q.set('fecha', fecha);
    if (empresaId != null) q.set('empresa_id', String(empresaId));
    const r = await peticion<{
      fecha: string;
      divisa: string;
      saldo_total: number;
      generado_en: string;
      empresas: {
        empresa_id: number;
        codigo: string;
        nombre: string;
        saldo_total: number;
        saldo_seguridad: number;
        bajo_umbral: boolean;
        cuentas: {
          cuenta_id: number;
          alias: string;
          entidad: string | null;
          iban: string;
          saldo: number;
          saldo_seguridad: number;
          bajo_umbral: boolean;
        }[];
      }[];
    }>(`/posicion${q.size ? `?${q}` : ''}`);

    return {
      fecha: r.fecha,
      saldo_consolidado: r.saldo_total,
      divisa: r.divisa,
      // El backend manda el IBAN entero; el kiosco solo enseña los 4 ultimos.
      empresas: r.empresas.map((e) => ({
        empresa_id: e.empresa_id,
        codigo: e.codigo,
        nombre: e.nombre,
        saldo_total: e.saldo_total,
        saldo_seguridad: e.saldo_seguridad,
        bajo_umbral: e.bajo_umbral,
        cuentas: e.cuentas.map((c) => ({
          cuenta_id: c.cuenta_id,
          alias: c.alias,
          entidad: c.entidad ?? '',
          iban_visible: `···${(c.iban ?? '').slice(-4)}`,
          saldo_actual: c.saldo,
          saldo_fecha: r.fecha,
          saldo_seguridad: c.saldo_seguridad,
          bajo_umbral: c.bajo_umbral,
        })),
      })),
      // TODO(Fase0): exponer en /api/posicion la fecha del ultimo N43 ingerido.
      ultima_ingesta: null,
    };
  },

  /**
   * GET /api/prevision — el backend razona en horizontes cerrados
   * (`13_semanas` | `12_meses`), no en dias sueltos; aqui se traduce.
   */
  prevision: async (horizonteDias = 91, escenarioId?: number): Promise<CurvaPrevision> => {
    const horizonte = horizonteDias > 120 ? '12_meses' : '13_semanas';
    const q = new URLSearchParams({ horizonte });
    if (escenarioId != null) q.set('escenario_id', String(escenarioId));
    const r = await peticion<{
      escenario_id: number | null;
      escenario_nombre: string | null;
      dia_minima_caja: string | null;
      minima_caja: number | null;
      puntos: {
        fecha_inicio: string;
        cobros: number;
        pagos: number;
        saldo_final: number;
        saldo_final_ponderado: number;
        saldo_seguridad: number;
      }[];
    }>(`/prevision?${q}`);

    return {
      horizonte_dias: horizonteDias,
      escenario_id: r.escenario_id,
      escenario_nombre: r.escenario_nombre,
      puntos: r.puntos.map((p) => ({
        fecha: p.fecha_inicio,
        saldo_previsto: p.saldo_final,
        // TODO(Fase3): el backend aun no distingue saldo real de previsto por
        // periodo; cuando lo haga, rellenar aqui.
        saldo_real: null,
        cobros: p.cobros,
        pagos: p.pagos,
        banda_min: p.saldo_final_ponderado,
        banda_max: p.saldo_final,
      })),
      minimo_fecha: r.dia_minima_caja,
      minimo_importe: r.minima_caja,
      umbral_seguridad: r.puntos[0]?.saldo_seguridad ?? null,
    };
  },

  /** GET /api/prevision/vencimientos — el parametro del backend es `dias`. */
  vencimientos: async (ventanaDias = 15): Promise<Vencimiento[]> => {
    const filas = await peticion<
      {
        prevision_id: number;
        fecha_prevista: string;
        importe: number;
        concepto: string;
        contrapartida: string | null;
        probabilidad: number;
      }[]
    >(`/prevision/vencimientos?dias=${ventanaDias}`);
    return filas.map((f) => ({
      prevision_id: f.prevision_id,
      fecha_prevista: f.fecha_prevista,
      importe: f.importe,
      concepto: f.concepto,
      contrapartida: f.contrapartida,
      probabilidad: f.probabilidad,
      // El endpoint solo devuelve previsiones vivas.
      estado: 'pendiente' as EstadoPrevision,
    }));
  },

  /**
   * GET /api/posicion/movimientos — `desde` y `hasta` son OBLIGATORIOS en el
   * backend (una consulta sin ventana puede barrer la tabla entera).
   */
  movimientosRecientes: async (limite = 20, ventanaDias = 90): Promise<MovimientoResumen[]> => {
    const q = new URLSearchParams({
      desde: haceDiasISO(ventanaDias),
      hasta: hoyISO(),
      limite: String(limite),
    });
    const filas = await peticion<
      {
        id: number;
        fecha_operacion: string;
        importe: number;
        concepto: string;
        categoria_id: number | null;
        clasificado_por: ClasificadoPor;
        conciliado: boolean;
      }[]
    >(`/posicion/movimientos?${q}`);
    return filas.map((m) => ({
      id: m.id,
      fecha_operacion: m.fecha_operacion,
      importe: m.importe,
      concepto: m.concepto,
      // TODO(Fase2): que el backend devuelva el nombre de la categoria, no solo el id.
      categoria_nombre: m.categoria_id == null ? null : String(m.categoria_id),
      clasificado_por: m.clasificado_por,
      conciliado: m.conciliado,
    }));
  },

  // --- Parte del día ---
  /**
   * GET /api/agente/parte — LEE el parte, no lo genera.
   *
   * Antes se llamaba a `POST /agente/despacho`, el mismo endpoint del cron de
   * las 07:00: abrir el kiosco relanzaba la ingesta entera y dos cargas en el
   * mismo segundo duplicaron 20 previsiones de Factusol (29/07/2026). Mirar la
   * pantalla no puede escribir en tesoreria. Poner al dia = cron o peticion
   * explicita.
   */
  parteDelDia: async (fecha?: string): Promise<ParteDia> => {
    const r = await peticion<{
      fecha: string;
      parte_hablado: string;
      parte_pantalla: string;
      hallazgos: Record<string, unknown>;
      propuestas_n2: number[];
      incidencias: string[];
    }>(`/agente/parte${fecha ? `?fecha=${encodeURIComponent(fecha)}` : ''}`);
    // El backend anida la posicion en `hallazgos.posicion` (ver
    // `despacho_diario._analizar`). Leerla de `hallazgos.posicion_consolidada`
    // —clave que nunca ha existido— dejaba el titular en 0,00 EUR mientras el
    // texto de al lado decia 1.800.914,68 EUR (reporte del jefe, 04/08/2026).
    const posicion = (r.hallazgos?.['posicion'] ?? {}) as Record<string, unknown>;
    const saldo = Number(posicion['saldo_total'] ?? NaN);
    // La variacion respecto a ayer NO la calcula todavia el despacho. Antes se
    // pintaba "+0,00 EUR desde ayer", que no es "no ha cambiado": es "no lo se".
    // `null` hace que no se pinte.
    const variacion = posicion['variacion_dia'];

    return {
      fecha: r.fecha,
      resumen_hablado: r.parte_hablado,
      detalle_markdown: r.parte_pantalla ?? '',
      posicion_consolidada: Number.isFinite(saldo) ? saldo : null,
      variacion_dia: variacion == null ? null : Number(variacion),
      alertas: (r.hallazgos?.['alertas'] as AlertaParte[] | undefined) ?? [],
      confirmaciones_pendientes: r.propuestas_n2.length,
      generado_en: new Date().toISOString(),
    };
  },

  // --- Confirmaciones Nivel 2 ---
  /**
   * Bandeja de propuestas pendientes.
   * El kiosco NUNCA ejecuta la herramienta: solo resuelve la confirmacion.
   */
  confirmacionesPendientes: (): Promise<Confirmacion[]> =>
    peticion<Confirmacion[]>('/confirmaciones?estado=pendiente'),

  /**
   * Resuelve una confirmacion.
   *
   * El backend tiene DOS endpoints separados (`/confirmar` y `/rechazar`), no
   * uno con parametro: confirmar emite el token efimero y rechazar lo anula.
   *
   * IMPORTANTE: la via legitima es el "si" verbal en sesion activa. `confirmar`
   * exige una transcripcion que pase `es_afirmacion()`; el boton del kiosco
   * manda un "si" explicito y queda marcado como via tactil.
   * TODO(Fase4): enviar la referencia de audio real (`audio_ref`) del "si".
   */
  resolverConfirmacion: async (
    id: number,
    decision: 'confirmada' | 'rechazada',
    _sesionId?: number,
  ): Promise<Confirmacion> => {
    if (decision === 'confirmada') {
      await peticion<{ id: number; token: string }>(`/confirmaciones/${id}/confirmar`, {
        method: 'POST',
        body: JSON.stringify({ transcripcion: 'si', audio_ref: null }),
      });
    } else {
      await peticion<unknown>(`/confirmaciones/${id}/rechazar`, {
        method: 'POST',
        body: JSON.stringify({ motivo: 'Rechazada desde el kiosco', audio_ref: null }),
      });
    }
    return peticion<Confirmacion>(`/confirmaciones/${id}`);
  },

  // --- Sesiones y conversacion ---
  /**
   * TODO(Fase4): el backend aun NO sirve `/api/sesiones`. Hoy la sesion nace
   * dentro del WebSocket `/api/voz/ws` (mensaje `{"tipo":"iniciar"}`) y vive en
   * memoria; esta funcion devuelve una sesion local para que el kiosco tenga un
   * id con el que trabajar mientras tanto.
   */
  abrirSesion: async (modo: ModoSesion = 'conversacion'): Promise<SesionVoz> => ({
    id: Date.now(),
    canal: 'kiosco',
    modo,
    estado: 'activa',
    iniciada_en: new Date().toISOString(),
  }),

  /** TODO(Fase4): cerrar la fila de `sesiones_voz` cuando el backend la cree. */
  cerrarSesion: async (_id: number): Promise<void> => undefined,

  /** TODO(Fase4): historico de turnos; requiere `/api/sesiones/{id}/turnos`. */
  turnos: async (_sesionId: number): Promise<Turno[]> => [],

  /**
   * POST /api/agente/chat — turno escrito con respuesta completa.
   * Para respuesta en streaming el backend ofrece `/api/agente/chat/stream` (SSE).
   */
  enviarTexto: async (sesionId: number, texto: string): Promise<Turno> => {
    const r = await peticion<{ texto: string; ts: string }>('/agente/chat', {
      method: 'POST',
      body: JSON.stringify({ mensaje: texto, sesion_id: sesionId, canal: 'kiosco' }),
    });
    return { id: `t-${Date.now()}`, orden: 0, rol: 'agente', texto: r.texto, ts: r.ts };
  },

  // --- Dictado (modo secretaria) ---
  /**
   * Cierra el dictado y pide al agente la version pulida.
   * En modo secretaria el agente SOLO escribe: no ejecuta herramientas.
   *
   * TODO(Fase4): no hay endpoint REST. El cierre real viaja por el WebSocket
   * (`{"tipo":"dictado","accion":"cerrar"}`) y el backend responde con la ruta
   * de la nota en la boveda. Aqui se usa `/api/agente/chat` en modo secretaria
   * para obtener la version pulida sin ejecutar ninguna herramienta.
   */
  cerrarDictado: async (
    sesionId: number,
    texto: string,
    exportarWord = false,
  ): Promise<ResultadoDictado> => {
    const r = await peticion<{ texto: string }>('/agente/chat', {
      method: 'POST',
      body: JSON.stringify({
        mensaje: texto,
        sesion_id: sesionId,
        modo: 'secretaria',
        canal: 'kiosco',
      }),
    });

    const rutaDocx = exportarWord ? await api.exportarDictadoWord(texto) : null;

    return {
      slug: '',
      ruta_nota: '',
      texto_literal: texto,
      texto_pulido: r.texto,
      ruta_docx: rutaDocx,
    };
  },

  /**
   * POST /api/voz/dictado/docx — genera el `.docx` del dictado en `salidas\`.
   *
   * Se llama desde LOS DOS caminos de cierre (por socket y por REST): el
   * dictado normal se cierra por el WebSocket, y esa rama se olvidaba de
   * generar el Word, asi que el boton "Guardar y hacer Word" no hacia nada
   * mientras el microfono estuviera vivo (reporte del jefe, 04/08/2026).
   *
   * Un fallo aqui NO se propaga: la nota ya esta a salvo en la boveda y
   * quedarse sin copia en Word no puede tumbar el cierre del dictado.
   */
  exportarDictadoWord: async (texto: string, titulo = 'Dictado'): Promise<string | null> => {
    try {
      const r = await peticion<{ ruta_docx: string }>('/voz/dictado/docx', {
        method: 'POST',
        body: JSON.stringify({ texto, titulo }),
      });
      return r.ruta_docx;
    } catch {
      return null;
    }
  },

  /**
   * POST /api/voz/muestra — sube el WAV grabado para clonar la voz.
   *
   * El servidor no tiene microfono: la muestra se graba en el navegador de
   * otro equipo y viaja por aqui. El backend la valida (duracion, que se oiga
   * algo, que sea un WAV) antes de guardarla.
   */
  subirMuestraVoz: async (wav: ArrayBuffer): Promise<{ guardada: boolean; ruta: string }> =>
    peticion<{ guardada: boolean; ruta: string }>('/voz/muestra', {
      method: 'POST',
      body: wav,
      headers: { 'Content-Type': 'audio/wav' },
    }),

  // --- Voces (probar motores en caliente) ---
  /** GET /api/voz/voces — catalogo de voces y cual esta activa. */
  voces: async (): Promise<{ activa: VozActiva; opciones: OpcionVoz[] }> =>
    peticion<{ activa: VozActiva; opciones: OpcionVoz[] }>('/voz/voces'),

  /** POST /api/voz/voces — cambia la voz para la siguiente frase. */
  elegirVoz: async (motor: string, voz: string): Promise<VozActiva> =>
    peticion<VozActiva>('/voz/voces', {
      method: 'POST',
      body: JSON.stringify({ motor, voz }),
    }),

  /** DELETE /api/voz/voces — vuelve a la voz de settings.ini. */
  restablecerVoz: async (): Promise<VozActiva> =>
    peticion<VozActiva>('/voz/voces', { method: 'DELETE' }),

  // --- Voz (TTS de un texto suelto, sin pasar por el agente) ---
  /**
   * POST /api/voz/tts — sintetiza un texto y devuelve su WAV.
   *
   * Para el boton "Léemelo en voz alta" del parte: lee un texto que ya existe
   * (`parte.resumen_hablado`) SIN invocar al agente. Devuelve el WAV crudo, que
   * el kiosco encola en el reproductor igual que la voz de la conversacion.
   */
  sintetizarVoz: async (texto: string): Promise<ArrayBuffer> => {
    const respuesta = await fetch(`${BASE_API}/api/voz/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ texto }),
    });
    if (!respuesta.ok) {
      if (respuesta.status === 401) _avisarSesionCaducada();
      throw new ErrorApi(respuesta.status, `El servicio respondio ${respuesta.status}`);
    }
    return respuesta.arrayBuffer();
  },

  // --- Ajustes de la IA (panel del kiosco) ---
  /** GET /api/ajustes — valores vigentes MAS las opciones disponibles. */
  ajustes: (): Promise<RespuestaAjustes> => peticion<RespuestaAjustes>('/ajustes'),

  /**
   * PUT /api/ajustes — guarda SOLO lo que cambia.
   *
   * El backend conserva lo que no venga (`aplicar_cambios`), asi que mandar el
   * objeto entero solo sirve para que rechace ajustes que el jefe no ha tocado.
   * Un valor invalido vuelve como 400 con el motivo ya en castellano.
   */
  guardarAjustes: (cambios: Partial<AjustesIA>): Promise<{ ajustes: AjustesIA }> =>
    peticion<{ ajustes: AjustesIA }>('/ajustes', {
      method: 'PUT',
      body: JSON.stringify({ cambios }),
    }),

  /** POST /api/ajustes/restablecer — vuelve a los valores de settings.ini. */
  restablecerAjustes: (): Promise<{ ajustes: AjustesIA }> =>
    peticion<{ ajustes: AjustesIA }>('/ajustes/restablecer', { method: 'POST' }),

  // --- Avatar ---
  /** GET /api/voz/estado — la configuracion del avatar viaja dentro. */
  configAvatar: async (): Promise<ConfigAvatar> => {
    const r = await peticion<{
      avatar: {
        activo: boolean;
        servicio_url: string;
        fps_minimo: number;
        modo_degradado: boolean;
        modo?: 'onda' | 'cara';
      };
    }>('/voz/estado');
    // Sin retrato definitivo, retrato_url va VACIO a proposito: el Avatar pinta
    // entonces su marcador de presencia (medallon), no la silueta de stock que
    // parecia un icono a medio terminar. Cuando exista retrato real (Fase 4) lo
    // publicara el backend en r.avatar.retrato_url y ganara este vacio.
    return { retrato_url: '', modo: 'onda' as const, ...r.avatar };
  },
};

// ---------------------------------------------------------------------------
// Canales en tiempo real (WebSocket)
// ---------------------------------------------------------------------------

/** Mensajes que el backend empuja por el canal de conversacion. */
export type MensajeConversacion =
  | { tipo: 'transcripcion_parcial'; texto: string }
  | { tipo: 'transcripcion_final'; texto: string; confianza: number | null }
  | { tipo: 'respuesta_parcial'; texto: string }
  | { tipo: 'respuesta_final'; turno: Turno }
  /**
   * Una frase de la respuesta ya sintetizada en voz. El WAV llega como trama
   * binaria (ArrayBuffer) por el mismo socket; `frecuencia` y `frase` vienen
   * del anuncio JSON que lo precede. El kiosco lo encola en el reproductor.
   */
  | { tipo: 'audio_respuesta'; wav: ArrayBuffer; frecuencia: number; frase: string }
  | { tipo: 'estado_agente'; estado: 'escuchando' | 'pensando' | 'hablando' | 'inactivo' }
  | { tipo: 'herramienta'; agente: AgenteRol; herramienta: string; nivel: 1 | 2 }
  | { tipo: 'confirmacion_nueva'; confirmacion: Confirmacion }
  /**
   * Eco del buffer de dictado tras cada comando de voz («punto y aparte»,
   * «borra la última frase»…). `textoActual` es la version AUTORITATIVA del
   * dictado: el kiosco pinta esa, no la suya, porque el backend ya ha aplicado
   * los comandos de edicion.
   *
   * Con `comando === 'cerrado'` el backend YA ha guardado la nota en la boveda
   * y manda las dos versiones (`textoLiteral` y `textoPulido`, esta ultima
   * calculada sin modelo) para que el jefe elija. Con `comando === 'version'`
   * viene la eleccion ya resuelta y `version` dice cual quedo como cuerpo.
   */
  | {
      tipo: 'dictado';
      comando: string;
      textoActual: string | null;
      respuesta: string;
      rutaBoveda: string | null;
      textoLiteral: string | null;
      textoPulido: string | null;
      version: 'literal' | 'pulida' | null;
    }
  /**
   * La charla se ha dado por terminada: por el boton «Finalizar conversación»
   * o porque el jefe lo dijo en voz alta. El socket se cierra justo despues,
   * asi que el kiosco NO debe tratarlo como una caida de red.
   */
  | { tipo: 'fin_conversacion'; texto: string }
  | { tipo: 'error'; mensaje: string };

/**
 * Traduce un evento del WebSocket del backend (`backend/api/voz.py`) al
 * contrato que consumen los componentes. Devuelve `null` para los eventos que
 * el kiosco ignora (`listo`, `dictado`).
 */
function _aMensajeConversacion(bruto: Record<string, unknown>): MensajeConversacion | null {
  const tipo = String(bruto.tipo ?? '');
  switch (tipo) {
    case 'transcripcion_parcial':
      return { tipo: 'transcripcion_parcial', texto: String(bruto.texto ?? '') };
    case 'transcripcion':
      return {
        tipo: 'transcripcion_final',
        texto: String(bruto.texto ?? ''),
        confianza: (bruto.confianza as number | null) ?? null,
      };
    case 'respuesta':
      return {
        tipo: 'respuesta_final',
        turno: {
          id: `t-${Date.now()}`,
          orden: 0,
          rol: 'agente',
          texto: String(bruto.texto ?? ''),
          ts: new Date().toISOString(),
        },
      };
    case 'estado': {
      // El backend tiene mas estados que el kiosco (dictando, esperando
      // confirmacion, cerrada): se pliegan a los cuatro que pinta el avatar.
      const crudo = String(bruto.estado ?? 'inactiva');
      const conocidos = ['escuchando', 'pensando', 'hablando'] as const;
      const estado = (conocidos as readonly string[]).includes(crudo)
        ? (crudo as 'escuchando' | 'pensando' | 'hablando')
        : ('inactivo' as const);
      return { tipo: 'estado_agente', estado };
    }
    case 'herramienta':
      return {
        tipo: 'herramienta',
        agente: 'orquestador',
        herramienta: String(bruto.nombre ?? ''),
        nivel: 1,
      };
    case 'dictado': {
      // `texto_actual` es el eco del buffer; en el evento de eleccion el
      // backend manda `texto` (la version elegida), que manda igual de fuerte.
      const actual = bruto.texto_actual ?? bruto.texto;
      const version = String(bruto.version ?? '');
      return {
        tipo: 'dictado',
        comando: String(bruto.comando ?? ''),
        textoActual: actual == null ? null : String(actual),
        respuesta: String(bruto.respuesta ?? ''),
        rutaBoveda: bruto.ruta_boveda == null ? null : String(bruto.ruta_boveda),
        textoLiteral: bruto.texto_literal == null ? null : String(bruto.texto_literal),
        textoPulido: bruto.texto_pulido == null ? null : String(bruto.texto_pulido),
        version: version === 'literal' || version === 'pulida' ? version : null,
      };
    }
    case 'respuesta_parcial':
      // Frase suelta que Androcles ya esta diciendo (habla mientras piensa).
      return { tipo: 'respuesta_parcial', texto: String(bruto.texto ?? '') };
    case 'fin_conversacion':
      return { tipo: 'fin_conversacion', texto: String(bruto.texto ?? '') };
    case 'error':
      return { tipo: 'error', mensaje: String(bruto.detalle ?? 'Error del servicio') };
    default:
      // `listo`, `confirmacion`: el kiosco recarga la bandeja con
      // `api.confirmacionesPendientes()`, no necesita el evento suelto.
      return null;
  }
}

/**
 * Mando del canal de conversacion.
 *
 * Es INVOCABLE: llamarlo cierra el socket, igual que la funcion de cierre que
 * devolvia antes esta API. Los metodos son la parte nueva (audio y control).
 */
export interface ControlConversacion {
  /** Cierra el socket. Compatibilidad con `const cerrar = conectar...()`. */
  (): void;
  /** Cierra el socket (nombre explicito). */
  cerrar: () => void;
  /**
   * Envia un fragmento de audio PCM 16 kHz mono 16 bits.
   * Devuelve false si el socket ya no admite datos.
   */
  enviarAudio: (pcm: ArrayBuffer) => boolean;
  /** Avisa de que el jefe ha dejado de hablar: procesa el turno. */
  finTurno: () => boolean;
  /** Turno escrito por teclado (alternativa a la voz). */
  enviarTexto: (texto: string) => boolean;
  /**
   * Entra en modo secretaria EN EL BACKEND.
   *
   * No es cosmetico: mientras el backend no esta en `DICTANDO`, lo que se diga
   * pasa por el orquestador. Abrir el dictado es lo que garantiza el
   * aislamiento de la seccion 4.4 (un dictado nunca es una orden).
   */
  abrirDictado: (titulo?: string) => boolean;
  /** Sale del modo secretaria; el backend guarda la nota en la boveda. */
  cerrarDictado: () => boolean;
  /**
   * Da la conversacion por terminada con despedida.
   *
   * No es lo mismo que `cerrar()`: aquel corta el socket en seco (cerrar la
   * pestana), mientras que esto pide al backend que se despida, cierre la fila
   * de `sesiones_voz` y avise con `fin_conversacion`.
   */
  finalizar: () => boolean;
  /** true si el socket admite envios ahora mismo. */
  estaAbierto: () => boolean;
}

/** Ajustes de apertura del canal. */
export interface OpcionesConversacion {
  /** Modo declarado al abrir sesion. Por defecto `conversacion`. */
  modo?: ModoSesion;
  /** Se llama con la sesion ya abierta; el sitio para `abrirDictado()`. */
  alAbrir?: (control: ControlConversacion) => void;
}

/**
 * Tope de la cola de envio mientras el socket conecta.
 * 300 fragmentos de 100 ms = 30 s de audio. Pasado eso se descarta lo viejo:
 * un socket que no abre no puede comerse la memoria del kiosco.
 */
const MAX_COLA_ENVIO = 300;

/**
 * Abre el canal de conversacion por voz/texto de una sesion.
 *
 * El backend expone UN unico socket por sesion en `/api/voz/ws`: la sesion se
 * abre enviando `{"tipo":"iniciar"}` nada mas conectar (no va en la URL). El
 * audio va por el mismo socket como tramas binarias.
 *
 * Lo que se envie antes de que el socket abra se encola y sale en orden: la
 * captura del microfono arranca sola y no debe perder las primeras palabras.
 *
 * TODO(Fase4): reconexion con backoff cuando el backend se reinicia.
 */
export function conectarConversacion(
  _sesionId: number,
  alRecibir: (mensaje: MensajeConversacion) => void,
  alCerrar?: (motivo: string) => void,
  opciones: OpcionesConversacion = {},
): ControlConversacion {
  const socket = new WebSocket(urlWebSocket('/api/voz/ws'));
  socket.binaryType = 'arraybuffer';

  /** Enviados antes de que el socket estuviera listo. */
  const cola: (string | ArrayBuffer)[] = [];

  const enviar = (dato: string | ArrayBuffer): boolean => {
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(dato);
      return true;
    }
    if (socket.readyState === WebSocket.CONNECTING) {
      if (cola.length >= MAX_COLA_ENVIO) cola.shift();
      cola.push(dato);
      return true;
    }
    return false;
  };

  const control = (() => {
    if (socket.readyState <= WebSocket.OPEN) socket.close();
  }) as ControlConversacion;

  control.cerrar = control;
  control.enviarAudio = (pcm) => enviar(pcm);
  control.finTurno = () => enviar(JSON.stringify({ tipo: 'fin_turno' }));
  control.enviarTexto = (texto) => enviar(JSON.stringify({ tipo: 'texto', texto }));
  control.abrirDictado = (titulo) =>
    enviar(JSON.stringify({ tipo: 'dictado', accion: 'abrir', titulo: titulo ?? null }));
  control.cerrarDictado = () => enviar(JSON.stringify({ tipo: 'dictado', accion: 'cerrar' }));
  control.finalizar = () => enviar(JSON.stringify({ tipo: 'finalizar' }));
  control.estaAbierto = () => socket.readyState === WebSocket.OPEN;

  socket.onopen = () => {
    socket.send(
      JSON.stringify({ tipo: 'iniciar', canal: 'kiosco', modo: opciones.modo ?? 'conversacion' }),
    );
    // Antes de soltar la cola: si esto abre el dictado, el audio encolado ya
    // entra en modo secretaria y no lo ve el orquestador.
    opciones.alAbrir?.(control);
    for (const pendiente of cola.splice(0)) socket.send(pendiente);
  };

  /**
   * Metadatos de la ultima frase de voz anunciada, a la espera de su WAV.
   *
   * El backend manda, por cada frase, un evento JSON `audio_respuesta` (formato
   * y frecuencia) y JUSTO DETRAS el WAV como trama binaria. El WebSocket
   * conserva el orden, asi que la trama binaria que llegue tras un anuncio es
   * su audio. Se guarda aqui hasta que llega el binario.
   */
  let audioPendiente: { frecuencia: number; frase: string } | null = null;

  socket.onmessage = (evento) => {
    // Trama binaria: es el WAV de la frase anunciada justo antes.
    if (typeof evento.data !== 'string') {
      const meta = audioPendiente;
      audioPendiente = null;
      if (meta && evento.data instanceof ArrayBuffer) {
        alRecibir({
          tipo: 'audio_respuesta',
          wav: evento.data,
          frecuencia: meta.frecuencia,
          frase: meta.frase,
        });
      }
      return;
    }
    try {
      const bruto = JSON.parse(evento.data) as Record<string, unknown>;
      // El anuncio de voz no es un turno: solo prepara la trama binaria que
      // viene detras. No se traduce a MensajeConversacion por si mismo.
      if (bruto.tipo === 'audio_respuesta') {
        audioPendiente = {
          frecuencia: Number(bruto.frecuencia ?? 22_050),
          frase: String(bruto.frase ?? ''),
        };
        return;
      }
      const traducido = _aMensajeConversacion(bruto);
      if (traducido) alRecibir(traducido);
    } catch {
      alRecibir({ tipo: 'error', mensaje: 'Mensaje del servidor ilegible' });
    }
  };
  socket.onerror = () => alRecibir({ tipo: 'error', mensaje: 'Se ha perdido la conexion' });
  socket.onclose = (evento) => {
    cola.length = 0;
    alCerrar?.(evento.reason || 'cerrado');
  };

  return control;
}
